import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { useCognitoAuth } from '@/contexts/AuthContext';
import { api } from '@/lib/api';
import type { BeangoError } from '@/lib/beango-error';
import { ErrorCode, userMessage } from '@/lib/error-codes';
import type { Account, AccountType } from '@/types';


const ACCOUNT_TYPE_VARIANTS = {
  ASSET: 'default',
  LIABILITY: 'destructive',
  EQUITY: 'secondary',
  REVENUE: 'outline',
  EXPENSE: 'outline',
} as const;

type BadgeVariant = 'default' | 'secondary' | 'destructive' | 'outline';

export function Accounts() {
  const { idToken } = useCognitoAuth();
  const [accounts, setAccounts] = useState<Account[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState('');
  const [search, setSearch] = useState('');
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    if (!idToken) return;
    async function fetchAccounts() {
      try {
        const data = await api.get<{ items: Account[] }>('/api/accounts', { errorCode: ErrorCode.FETCH_ACCOUNTS, token: idToken! });
        setAccounts(data.items ?? []);
        setError(null);
      } catch (err) {
        setError(err instanceof Error && 'code' in err ? userMessage((err as BeangoError).code) : 'Failed to fetch accounts');
      } finally {
        setLoading(false);
      }
    }
    setLoading(true);
    void fetchAccounts();
  }, [retryKey, idToken]);

  const filteredAccounts = accounts.filter((account) => {
    const matchesType = !filter || account.type === filter;
    const matchesSearch = !search ||
      account.name.toLowerCase().includes(search.toLowerCase()) ||
      account.code.toLowerCase().includes(search.toLowerCase());
    return matchesType && matchesSearch;
  });

  const totalsByType = accounts.reduce((acc, account) => {
    if (!acc[account.type]) {
      acc[account.type] = 0;
    }
    return acc;
  }, {} as Record<AccountType, number>);

  const formatCurrency = useCallback((amount: number) => {
    return new Intl.NumberFormat('en-CA', {
      style: 'currency',
      currency: 'CAD',
    }).format(Math.abs(amount));
  }, []);

  const handleFilterChange = useCallback((e: React.ChangeEvent<HTMLSelectElement>) => {
    setFilter(e.target.value);
  }, []);

  const handleSearchChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    setSearch(e.target.value);
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center p-12 text-muted-foreground">
        <i className="bi bi-arrow-repeat text-2xl mb-3 animate-spin"></i>
        <p>Loading accounts...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-12">
        <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-lg text-destructive text-sm mb-4">{error}</div>
        <button className="text-sm text-primary hover:underline" onClick={() => { setError(null); setRetryKey(k => k + 1); }}>
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-6">
      <div className="flex justify-between items-center pb-4 border-b border-border">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Chart of Accounts</h1>
          <p className="text-sm text-muted-foreground">{accounts.length} accounts</p>
        </div>
        <Button variant="default">
          <i className="bi bi-plus mr-2"></i> Add Account
        </Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Assets</p>
            <p className="text-2xl font-bold font-mono" style={{ color: 'var(--color-primary)' }}>
              {formatCurrency(totalsByType.ASSET ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Liabilities</p>
            <p className="text-2xl font-bold font-mono text-red-600">
              {formatCurrency(totalsByType.LIABILITY ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Total Equity</p>
            <p className="text-2xl font-bold font-mono text-purple-600">
              {formatCurrency(totalsByType.EQUITY ?? 0)}
            </p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <p className="text-sm text-muted-foreground mb-1">Net Income</p>
            <p className="text-2xl font-bold font-mono text-green-600">
              {formatCurrency((totalsByType.REVENUE ?? 0) - (totalsByType.EXPENSE ?? 0))}
            </p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="pt-6">
          <div className="flex gap-4 flex-wrap">
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Account Type</label>
              <select
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                value={filter}
                onChange={handleFilterChange}
              >
                <option value="">All Types</option>
                <option value="ASSET">Assets</option>
                <option value="LIABILITY">Liabilities</option>
                <option value="EQUITY">Equity</option>
                <option value="REVENUE">Revenue</option>
                <option value="EXPENSE">Expenses</option>
              </select>
            </div>
            <div className="flex flex-col gap-1">
              <label className="text-xs font-medium text-muted-foreground">Search</label>
              <input
                type="text"
                className="px-3 py-2 border border-border rounded-md bg-card text-foreground text-sm"
                placeholder="Search accounts..."
                value={search}
                onChange={handleSearchChange}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          Accounts ({filteredAccounts.length})
        </CardHeader>
        <CardContent>
          {filteredAccounts.length > 0 ? (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-border">
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Code</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Name</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Type</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Subtype</th>
                    <th className="text-right text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Balance</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Status</th>
                    <th className="text-left text-xs font-semibold text-muted-foreground uppercase tracking-wider px-4 py-3">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredAccounts.map((account) => (
                    <tr key={account.id} className="border-b border-border hover:bg-muted/50 transition-colors">
                      <td className="px-4 py-3">
                        <code className="text-sm">{account.code}</code>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/accounts/${account.id}`} className="text-foreground hover:text-primary">
                          {account.name}
                        </Link>
                      </td>
                      <td className="px-4 py-3">
                        <Badge variant={ACCOUNT_TYPE_VARIANTS[account.type as keyof typeof ACCOUNT_TYPE_VARIANTS] as BadgeVariant}>
                          {account.type}
                        </Badge>
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{account.subtype ?? '-'}</td>
                      <td className="px-4 py-3">
                        <Badge variant={account.is_active ? 'secondary' : 'outline'}>
                          {account.is_active ? 'Active' : 'Inactive'}
                        </Badge>
                      </td>
                      <td className="px-4 py-3">
                        <Link to={`/accounts/${account.id}`}>
                          <Button variant="ghost" size="sm">View</Button>
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div className="text-center py-12">
              <i className="bi bi-folder2-open text-4xl text-muted-foreground mb-3"></i>
              <h3 className="text-lg font-semibold text-foreground mb-1">No accounts found</h3>
              <p className="text-muted-foreground mb-4">
                {search || filter ? 'Try adjusting your filters' : 'Get started by adding your chart of accounts'}
              </p>
              {!search && !filter && (
                <Button variant="default">
                  <i className="bi bi-plus mr-2"></i> Add Account
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
